// src/lib/storage/KVOperations.ts
import { BaseOperations } from './base-opreations.ts';
import { LocalDatabase } from './local-database.ts';
import {
  GetOptions,
  isKVQueuePayload,
  KVOperationType,
  KVQueuePayload,
  KVValue,
  RemoteClient,
  SyncQueueItem
} from './types';
import { syncEventEmitter } from './sync-event-emitter.ts';

export class KVOperations extends BaseOperations {
  constructor(
    db: LocalDatabase,
    remoteClient: RemoteClient | null
  ) {
    super(db, remoteClient, ['set', 'del']);
  }


  async syncFromRemote(key: string): Promise<boolean> {
    if (!this.remoteClient || !navigator.onLine) {
      return false;
    }

    try {
      const value = await this.fetchWithTimeout(
        () => this.remoteClient ? this.remoteClient.get(key) : Promise.resolve(null)
      );

      if (value === null) {
        await this.db.kvStore.delete(key);
      } else {
        await this.db.kvStore.put({
          key,
          value,
          timestamp: Date.now()
        });
      }
      return true;
    } catch (error) {
      console.error('Remote sync failed for key:', key, error);
      return false;
    }
  }

  /**
   * Get a value from storage
   */
  async get<T>(key: string, options: GetOptions<T> = {}): Promise<T | null> {
    // Helper function to parse value if needed
    const parseValue = (value: unknown): T | null => {
      if (value === null) return null;

      if (typeof value === 'string') {
        try {
          return JSON.parse(value) as T;
        } catch (e) {
          // Only warn if it looked like JSON but failed to parse
          if (value.startsWith('{') || value.startsWith('[')) {
            console.warn('Failed to parse cached value:', e);
          }
          return value as T;
        }
      }

      // If it's already an object (from Vercel KV), return as is
      return value as T;
    };

    // Helper function to fetch from remote and update cache
    // Returns { success: boolean, value: T | null }
    // success=true means remote responded (value may be null if deleted)
    // success=false means network/timeout error (should fall back to cache)
    const fetchAndUpdateCache = async (): Promise<{ success: boolean; value: T | null }> => {
      if (!this.remoteClient) return { success: false, value: null };

      try {
        const value = await this.fetchWithTimeout<KVValue<T>>(
          async () => (await this.remoteClient?.get<T>(key)) ?? null
        );
        if (value !== null) {
          await this.db.kvStore.put({
            key,
            value,
            timestamp: Date.now()
          });
        } else {
          // Remove from local if not in remote (successful fetch returned null = deleted)
          await this.db.kvStore.delete(key);
        }
        return { success: true, value: parseValue(value) };
      } catch (error) {
        console.warn('Remote fetch failed:', error);
        // Return failure - do NOT delete local cache on network errors
        return { success: false, value: null };
      }
    };

    // If force refresh is requested, and we're online, try remote first
    if (options.forceRefresh && navigator.onLine && this.remoteClient) {
      const result = await fetchAndUpdateCache();
      if (result.success) {
        // Emit data source event
        syncEventEmitter.emitDataSource(key, 'remote');
        // Remote fetch succeeded - return the value (even if null = deleted)
        return result.value;
      }
      // Remote fetch failed - fall back to cache below
      console.warn(`Force refresh failed for key ${key}, falling back to cache`);
    }

    // Get from local storage
    const cached = await this.db.kvStore.get(key);
    // If not in cache, and we haven't tried remote yet, try remote
    if (!cached && navigator.onLine && this.remoteClient && !options.forceRefresh) {
      const result = await fetchAndUpdateCache();
      if (result.success) {
        // Emit data source event
        syncEventEmitter.emitDataSource(key, 'remote');
        // Remote fetch succeeded - return the value (may be null if not found)
        return result.value;
      }
      // Remote fetch failed - return null (nothing in cache, couldn't fetch)
    }
    // If we're online and background refresh is requested, update cache in background
    else if (cached && navigator.onLine && this.remoteClient && options.backgroundRefresh) {
      // Emit cache event first (returning cached data)
      syncEventEmitter.emitDataSource(key, 'cache', cached.timestamp);
      // Then refresh in background
      this.refreshCache(key)?.then(() => {
        // Emit remote event when background refresh completes
        syncEventEmitter.emitDataSource(key, 'remote');
      }).catch(error =>
        console.warn('Background refresh failed:', error)
      );
    }
    // Returning cached data
    else if (cached) {
      syncEventEmitter.emitDataSource(key, 'cache', cached.timestamp);
    }

    return cached ? parseValue(cached.value) : null;
  }

  /**
   * Set a value in storage
   */
  async set<T>(key: string, value: T): Promise<void> {
    // Update local storage immediately
    await this.db.kvStore.put({
      key,
      value,
      timestamp: Date.now()
    });

    // Handle remote sync and device notification in the background
    await this.syncInBackground(key, { value }, 'set').catch(error =>
      console.warn('Background sync failed:', error)
    );
  }

  async delete(key: string): Promise<void> {
    // Delete from local storage immediately
    await this.db.kvStore.delete(key);

    // Handle remote sync and device notification in the background
    await this.syncInBackground(key, null, 'del').catch(error =>
      console.warn('Background sync failed:', error)
    );
  }

  async scan(cursor = 0, options: { match?: string; count?: number } = {}): Promise<[number, string[]]> {
    console.log('Scan called with cursor:', cursor, 'options:', options);
    console.log('Online status:', navigator.onLine);
    console.log('Remote client available:', !!this.remoteClient);


    // const result = await this.remoteClient.scan(0, {
    //   match: 'user:fe0667ed-aa71-405d-897b-376bd30ec87c:exercise:*',
    //   count: 100
    // });
    // console.log('Direct Vercel KV scan result:', result);
    let keys: string[] = [];

    // If we're online and have a remote client, try remote first
    if (navigator.onLine && this.remoteClient) {
      try {
        const [_, remoteKeys] = await this.fetchWithTimeout<[number, string[]]>(() => {
          if (!this.remoteClient) {
            return Promise.reject(new Error('Remote client is unavailable'));
          }
          console.log('About to call remote scan');
          const result = this.remoteClient.scan(0, options);
          console.log('Remote scan result:', result);
          return result;
        });
        console.log('Remote keys:', remoteKeys);
        keys = remoteKeys;
      } catch (error) {
        console.error('Remote scan operation failed:', error);
        // Fall through to local scan if remote fails
      }
    }

    // If remote failed, or we're offline, use local storage
    if (keys.length === 0) {
      const allKeys = await this.db.kvStore.toCollection().primaryKeys();
      keys = allKeys.map(key => key.toString());
    }

    // Handle Redis-style pattern matching
    if (options.match) {
      const pattern = options.match
        .replace(/\*/g, '.*')
        .replace(/\?/g, '.')
        .replace(/\[(\w-\w)]/g, '[$1]');
      const regex = new RegExp(`^${pattern}$`);
      keys = keys.filter(key => regex.test(key));
    }

    // Handle pagination
    const count = options.count ?? 10;
    const start = cursor;
    const end = Math.min(start + count, keys.length);
    const nextCursor = end >= keys.length ? 0 : end;

    return [nextCursor, keys.slice(start, end)];
  }

  private async syncInBackground(
    key: string,
    payload: KVQueuePayload,
    operation: KVOperationType
  ): Promise<void> {
    if (navigator.onLine && this.remoteClient) {
      try {
        if (operation === 'set' && payload) {
          await this.fetchWithTimeout(() => {
            if (!this.remoteClient) {
              throw new Error('Remote client is unavailable');
            }
            return this.remoteClient.set(key, payload.value);
          });
        } else if (operation === 'del') {
          await this.fetchWithTimeout(() => {
            if (!this.remoteClient) {
              throw new Error('Remote client is unavailable');
            }
            return this.remoteClient.del(key);
          });
        }
      } catch (error) {
        console.warn(`Background sync failed for ${operation}:`, error);
        await this.queueOperation(operation, key, payload);
      }
    } else {
      await this.queueOperation(operation, key, payload);
    }
  }

  /**
   * Execute remote operations from sync queue
   */
  protected async executeRemoteOperation(item: SyncQueueItem): Promise<void> {
    if (!this.remoteClient) {
      throw new Error('Remote client not available');
    }

    if (!isKVQueuePayload(item.operation, item.payload)) {
      throw new Error('Not valid kv operation payload for execute remote operation');
    }

    try {
      switch (item.operation) {
        case 'set': {
          const payload = item.payload;
          if (!payload) {
            throw new Error('Invalid set payload in queue');
          }
          await this.remoteClient.set(item.key, payload.value);
          break;
        }
        case 'del':
          await this.remoteClient.del(item.key);
          break;
        default:
          throw new Error(`Unsupported operation: ${item.operation}`);
      }
    } catch (error) {
      console.error('Failed to execute remote operation:', {
        operation: item.operation,
        key: item.key,
        error
      });
      throw error;
    }
  }

  /**
   * Refresh the cache in the background
   */
  private async refreshCache(key: string): Promise<void> {
    if (!this.remoteClient) return;

    try {
      const value = await this.remoteClient.get(key);

      if (value === null) {
        await this.db.kvStore.delete(key);
      } else {
        await this.db.kvStore.put({
          key,
          value,
          timestamp: Date.now()
        });
      }
    } catch (error) {
      console.warn('Cache refresh failed:', error);
    }
  }
}